import axios from 'axios';

// Configuración de FileMaker desde variables de entorno
const FILEMAKER_CONFIG = {
  apiUrl: process.env.REACT_APP_FILEMAKER_API_URL,
  apiToken: process.env.REACT_APP_FILEMAKER_API_TOKEN,
  database: process.env.REACT_APP_FILEMAKER_DATABASE || 'yooladmin',
  timeout: 10000
};

/**
 * Servicio base para interactuar con FileMaker Data API
 */
class FileMakerService {
  constructor() {
    this.baseURL = FILEMAKER_CONFIG.apiUrl;
    this.token = FILEMAKER_CONFIG.apiToken;
    this.database = FILEMAKER_CONFIG.database;
  }

  /**
   * Configuración base para las peticiones HTTP
   */
  getRequestConfig() {
    return {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      timeout: FILEMAKER_CONFIG.timeout
    };
  }

  /**
   * Buscar registros en una tabla/layout específico
   * @param {string} layout - Nombre del layout en FileMaker
   * @param {object} options - Opciones de búsqueda (query, sort, limit, offset)
   * @returns {Promise<object>} - Respuesta con los registros encontrados
   */
  async findRecords(layout, options = {}) {
    try {
      const { query, sort, limit = 100, offset = 1 } = options;
      
      const requestBody = {
        query: query || [],
        sort: sort || [],
        limit,
        offset
      };

      const response = await axios.post(
        `${this.baseURL}/fmi/data/vLatest/databases/${this.database}/layouts/${layout}/_find`,
        requestBody,
        this.getRequestConfig()
      );

      return {
        success: true,
        data: response.data.response.data || [],
        foundCount: response.data.response.foundCount || 0,
        returnedCount: response.data.response.returnedCount || 0
      };
    } catch (error) {
      console.error(`Error finding records in layout ${layout}:`, error);
      
      if (error.response?.status === 401) {
        throw new Error('Error de autenticación con FileMaker. Verifica el token de API.');
      }
      
      if (error.response?.status === 404) {
        throw new Error(`Layout '${layout}' no encontrado en la base de datos.`);
      }
      
      throw new Error(`Error al buscar registros: ${error.message}`);
    }
  }

  /**
   * Obtener un registro específico por ID
   * @param {string} layout - Nombre del layout en FileMaker
   * @param {string|number} recordId - ID del registro
   * @returns {Promise<object>} - Respuesta con el registro
   */
  async getRecord(layout, recordId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/fmi/data/vLatest/databases/${this.database}/layouts/${layout}/records/${recordId}`,
        this.getRequestConfig()
      );

      return {
        success: true,
        data: response.data.response.data[0] || null
      };
    } catch (error) {
      console.error(`Error getting record ${recordId} from layout ${layout}:`, error);
      
      if (error.response?.status === 401) {
        throw new Error('Error de autenticación con FileMaker. Verifica el token de API.');
      }
      
      if (error.response?.status === 404) {
        throw new Error(`Registro con ID '${recordId}' no encontrado.`);
      }
      
      throw new Error(`Error al obtener registro: ${error.message}`);
    }
  }

  /**
   * Crear un nuevo registro
   * @param {string} layout - Nombre del layout en FileMaker
   * @param {object} fieldData - Datos del registro a crear
   * @returns {Promise<object>} - Respuesta con el registro creado
   */
  async createRecord(layout, fieldData) {
    try {
      const response = await axios.post(
        `${this.baseURL}/fmi/data/vLatest/databases/${this.database}/layouts/${layout}/records`,
        { fieldData },
        this.getRequestConfig()
      );

      return {
        success: true,
        recordId: response.data.response.recordId,
        modId: response.data.response.modId
      };
    } catch (error) {
      const fmMsg = Array.isArray(error?.response?.data?.messages) ? error.response.data.messages[0] : null;
      const fmCode = fmMsg?.code;
      const fmText = fmMsg?.message;
      const detail = fmCode || fmText ? ` (${[fmCode, fmText].filter(Boolean).join(" ")})` : "";

      console.error(`Error creating record in layout ${layout}:`, error?.response?.data || error);
      throw new Error(`Error al crear registro: ${error.message}${detail}`);
    }
  }

  /**
   * Actualizar un registro existente
   * @param {string} layout - Nombre del layout en FileMaker
   * @param {string|number} recordId - ID del registro
   * @param {object} fieldData - Datos a actualizar
   * @returns {Promise<object>} - Respuesta con el registro actualizado
   */
  async updateRecord(layout, recordId, fieldData) {
    try {
      const response = await axios.patch(
        `${this.baseURL}/fmi/data/vLatest/databases/${this.database}/layouts/${layout}/records/${recordId}`,
        { fieldData },
        this.getRequestConfig()
      );

      return {
        success: true,
        modId: response.data.response.modId
      };
    } catch (error) {
      console.error(`Error updating record ${recordId} in layout ${layout}:`, error);
      throw new Error(`Error al actualizar registro: ${error.message}`);
    }
  }

  async runScript(layout, recordId, script, scriptParam = "") {
    try {
      const scriptName = String(script || "").trim();
      if (!scriptName) {
        throw new Error("Nombre de script inválido");
      }

      const response = await axios.get(
        `${this.baseURL}/fmi/data/vLatest/databases/${this.database}/layouts/${layout}/records/${recordId}`,
        {
          ...this.getRequestConfig(),
          params: {
            script: scriptName,
            "script.param": String(scriptParam ?? ""),
          },
        }
      );

      const resp = response?.data?.response || {};

      return {
        success: true,
        scriptResult: resp?.scriptResult,
        scriptError: resp?.scriptError,
        data: resp?.data?.[0] || null,
      };
    } catch (error) {
      console.error(`Error running script '${script}' in layout ${layout}:`, error?.response?.data || error);
      throw new Error(`Error al ejecutar script: ${error.message}`);
    }
  }

  /**
   * Eliminar un registro
   * @param {string} layout - Nombre del layout en FileMaker
   * @param {string|number} recordId - ID del registro
   * @returns {Promise<object>} - Respuesta de confirmación
   */
  async deleteRecord(layout, recordId) {
    try {
      await axios.delete(
        `${this.baseURL}/fmi/data/vLatest/databases/${this.database}/layouts/${layout}/records/${recordId}`,
        this.getRequestConfig()
      );

      return {
        success: true,
        message: 'Registro eliminado correctamente'
      };
    } catch (error) {
      console.error(`Error deleting record ${recordId} from layout ${layout}:`, error);
      throw new Error(`Error al eliminar registro: ${error.message}`);
    }
  }

  /**
   * Obtener todos los registros de un layout (sin filtros)
   * @param {string} layout - Nombre del layout en FileMaker
   * @param {object} options - Opciones adicionales (sort, limit)
   * @returns {Promise<object>} - Respuesta con todos los registros
   */
  async getAllRecords(layout, options = {}) {
    try {
      // URL simple sin parámetros problemáticos
      const response = await axios.get(
        `${this.baseURL}/fmi/data/vLatest/databases/${this.database}/layouts/${layout}/records`,
        this.getRequestConfig()
      );

      return {
        success: true,
        data: response.data.response.data || [],
        foundCount: response.data.response.foundCount || 0,
        returnedCount: response.data.response.returnedCount || 0
      };
    } catch (error) {
      console.error(`Error getting all records from layout ${layout}:`, error);
      throw new Error(`Error al obtener registros: ${error.message}`);
    }
  }

  /**
   * Verificar la conectividad con FileMaker
   * @returns {Promise<boolean>} - true si la conexión es exitosa
   */
  async testConnection() {
    try {
      const response = await axios.get(
        `${this.baseURL}/fmi/data/vLatest/databases/${this.database}/layouts`,
        this.getRequestConfig()
      );
      
      return response.status === 200;
    } catch (error) {
      console.error('Error testing FileMaker connection:', error);
      return false;
    }
  }
}

// Crear y exportar una instancia del servicio
const filemakerService = new FileMakerService();

export { filemakerService };
export default filemakerService;
